import { IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import EmailIcon from "src/assets/svg-icons/email.svg";
import CloseIcon from "src/assets/svg-icons/close_trans.svg";
import WhatsAppIcon from "src/assets/svg-icons/whatsapp.svg";
import CalcIcon from "src/assets/svg-icons/logo.svg";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { specificBranchHasPermission } from "src/utils";
import { SalesPermissions } from "src/constants/permissions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { useSendPoMutation } from "src/api/inventory/reorder";
import {
  useDeleteInvoiceMutation,
  usePrintInvoiceMutation,
  useResubmitInvoiceMutation,
  useReturnInvoiceMutation,
} from "src/api/sales/invoices";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import AddInvoicePaymentPopup from "../../pay-popup";
import ReasonPopup from "../../reaosn-popup";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";

interface IAction {
  row: any;
  refetch: Function;
}

const TableActions = (props: IAction) => {
  const { row, refetch } = props;
  const { id, invoice_status, send_method, branch_id, type, zatca } = row;
  const { t } = useTranslation("inventory");
  const { t: generalT } = useTranslation("general");
  const { t: salesT } = useTranslation("addSales");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [actionType, setActionType] = useState<string>();
  const [openPay, setOpenPay] = useState(false);
  const [openReason, setOpenReason] = useState(false);

  const isCancelled = invoice_status === "cancelled";
  const isSubmitted = invoice_status === "submitted";
  const isInvoice = type === "invoice";
  const isCreditNote = type === "credit_note";
  const isZatcaRejected = zatca?.status === "rejected";

  // APIS
  const { mutate, isPending } = useReturnInvoiceMutation();
  const { mutate: mutateResubmit, isPending: isPendingResubmit } =
    useResubmitInvoiceMutation();
  const {
    mutate: mutateSend,
    error: errorSend,
    status: statusSend,
    isPending: isPendingSend,
  } = useSendPoMutation();
  const { mutate: mutateDelete, isPending: isPendingDelete } =
    useDeleteInvoiceMutation();
  const { mutateAsync: printInvoice } = usePrintInvoiceMutation();

  //permissions
  const returnPermission = specificBranchHasPermission(
    SalesPermissions.returnSaleInvoice,
    branch_id,
  );
  const payPermission = specificBranchHasPermission(
    SalesPermissions.paySaleInvoice,
    branch_id,
  );
  const addPermission = specificBranchHasPermission(
    SalesPermissions.addSaleInvoice,
    branch_id,
  );

  const handleOpenConfirm = (type: string) => {
    setActionType(type);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenPay = () => setOpenPay(true);
  const handleClosePay = () => setOpenPay(false);

  const handleOpenReason = () => setOpenReason(true);
  const handleCloseReason = () => setOpenReason(false);

  const handlePrint = async () => {
    handleClose();
    try {
      const response = await printInvoice({ id });
      const blob = new Blob([response as unknown as BlobPart], {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setNotifications([
        ...notifications,
        { type: "error", message: generalT("somethingWrong") },
      ]);
    }
  };

  const navigateToPoDetails = () => {
    navigate(`/sales/invoices/${id}`);
  };

  const handleConfirm = () => {
    if (actionType === "return") {
      mutate(
        { id },
        {
          onSuccess: (response) => {
            navigate(`/sales/invoices/${response?.data?.sale_invoice_id}`);
          },
          onError: (error) => {
            setNotifications([
              ...notifications,
              {
                type: "error",
                message: error?.data?.errors?.[0].value || t("somethingWrong"),
              },
            ]);
          },
        },
      );
    } else if (actionType === "resubmit") {
      mutateResubmit(
        { id },
        {
          onSuccess: (response) => {
            navigate(`/sales/invoices/${response?.data?.sale_invoice_id}`);
          },
          onError: (error) => {
            setNotifications([
              ...notifications,
              {
                type: "error",
                message: error?.data?.errors?.[0].value || t("somethingWrong"),
              },
            ]);
          },
        },
      );
    } else if (actionType === "sendApp") {
      mutateSend({
        id,
        send_method: send_method === "app" ? "not_sent" : "app",
      });
    } else if (actionType === "sendEmail") {
      mutateSend({
        id,
        send_method: send_method === "email" ? "not_sent" : "email",
      });
    } else if (actionType === "cancelAndCopy") {
      navigate(`/sales/invoices/${id}/copy`);
    } else if (actionType === "delete") {
      mutateDelete(id, {
        onSuccess: () => {
          refetch();
          handleCloseConfirm();
          setNotifications([
            ...notifications,
            {
              type: "success",
              message: t("deletedSuccessfully"),
            },
          ]);
        },
        onError: (error) => {
          setNotifications([
            ...notifications,
            {
              type: "error",
              message: error?.data?.errors?.[0].value || t("somethingWrong"),
            },
          ]);
        },
      });
    }
  };

  useEffect(() => {
    if (statusSend === "success") {
      refetch();
      handleCloseConfirm();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("done"),
        },
      ]);
    } else if (statusSend === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: errorSend?.data.errors[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [statusSend]);

  return (
    <Stack direction={"row"} spacing={"12px"} justifyContent={"center"}>
      <IconButton size="small" onClick={(e) => handleClick(e)}>
        <MoreVertIcon />
      </IconButton>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          // onClick={(e) => handleOpenConfirm("sendEmail")}
          sx={{ gap: 1 }}
          disabled={isCancelled || isZatcaRejected}
        >
          <img width={17} src={EmailIcon} alt="" />
          <Typography
            color={"#1D2939"}
            fontSize={"16px"}
            textTransform={"capitalize"}
          >
            {t("sendEmail")}
          </Typography>
        </MenuItem>

        <MenuItem
          // onClick={(e) => handleOpenConfirm("sendApp")}
          sx={{ gap: 1 }}
          disabled={isCancelled || isZatcaRejected}
        >
          <img width={16} src={CalcIcon} alt="" />
          <Typography
            color={"#1D2939"}
            fontSize={"16px"}
            textTransform={"capitalize"}
          >
            {t("sendApp")}
          </Typography>
        </MenuItem>

        <MenuItem sx={{ gap: 1 }} disabled={isCancelled || isZatcaRejected}>
          <img width={15} src={WhatsAppIcon} alt="" />
          <Typography
            color={"#1D2939"}
            fontSize={"16px"}
            textTransform={"capitalize"}
          >
            {t("shareWhatsApp")}
          </Typography>
        </MenuItem>

        <MenuItem onClick={navigateToPoDetails} sx={{ gap: 1 }}>
          <VisibilityOutlinedIcon sx={{ fontSize: "16px" }} />
          <Typography
            color={"#1D2939"}
            fontSize={"16px"}
            textTransform={"capitalize"}
          >
            {t("details")}
          </Typography>
        </MenuItem>

        <MenuItem onClick={handlePrint} sx={{ gap: 1 }} disabled={isCancelled}>
          <PrintOutlinedIcon sx={{ fontSize: "16px" }} />
          <Typography color={"#1D2939"} fontSize={"16px"}>
            {generalT("print")}
          </Typography>
        </MenuItem>
        {!isZatcaRejected && (
          <MenuItem onClick={(e) => {}} sx={{ gap: 1 }}>
            <img src={DownloadIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {generalT("download")}
            </Typography>
          </MenuItem>
        )}

        {returnPermission && !isCancelled && isInvoice && isSubmitted && (
          <MenuItem
            sx={{ gap: 1 }}
            onClick={(e) => handleOpenConfirm("return")}
          >
            <img width={17} src={CloseIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {salesT("return")}
            </Typography>
          </MenuItem>
        )}

        {payPermission &&
          row?.payment_status !== "Paid" &&
          row.invoice_status !== "draft" &&
          !isCancelled &&
          !isZatcaRejected &&
          !(isCreditNote && !row.is_refundable) && (
            <MenuItem onClick={handleOpenPay} sx={{ gap: 1 }}>
              <PaidOutlinedIcon sx={{ fontSize: "16px" }} />
              <Typography
                color={"#1D2939"}
                fontSize={"16px"}
                textTransform={"capitalize"}
              >
                {isCreditNote ? generalT("refund") : `${t("pay")}!`}
              </Typography>
            </MenuItem>
          )}

        {!!row?.zatca?.reason && (
          <MenuItem onClick={handleOpenReason} sx={{ gap: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: "16px" }} />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {generalT("reason")}
            </Typography>
          </MenuItem>
        )}

        {isZatcaRejected && (
          <MenuItem
            sx={{ gap: 1 }}
            onClick={() => handleOpenConfirm("resubmit")}
          >
            <img width={17} src={CloseIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {salesT("resubmit")}
            </Typography>
          </MenuItem>
        )}

        {row.invoice_status === "draft" && (
          <MenuItem sx={{ gap: 1 }} onClick={() => handleOpenConfirm("delete")}>
            <img src={DeleteIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {generalT("delete")}
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* payment popup */}
      {openPay && (
        <AddInvoicePaymentPopup
          open={openPay}
          handleClose={handleClosePay}
          refetch={refetch}
          row={row}
        />
      )}

      {/* reason popup */}
      {openReason && (
        <ReasonPopup
          open={openReason}
          handleClose={handleCloseReason}
          reason={row?.zatca?.reason ?? ""}
        />
      )}

      {/* confirm popup */}
      <ConfirmPopup
        open={openConfirm}
        item={""}
        handleClose={handleCloseConfirm}
        handleConfirm={handleConfirm}
        isPending={
          isPending || isPendingSend || isPendingResubmit || isPendingDelete
        }
        title={
          actionType === "sendApp"
            ? t("sendApp")
            : actionType === "sendEmail"
              ? t("sendEmail")
              : actionType === "return"
                ? salesT("return")
                : actionType === "resubmit"
                  ? salesT("resubmit")
                  : generalT("delete")
        }
        subtitle={generalT("areYouSureToConfirm")}
        confirmBtnText={generalT("confirm")}
        noIcon
      />
    </Stack>
  );
};
export default TableActions;
